import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  adminOrderService,
  type AdminOrder,
} from '../../services/adminOrderService';
import {
  adminSubscriptionService,
  type AdminSubscription,
} from '../../services/adminSubscriptionService';
import {
  adminTeamWorkspaceService,
  type TeamSubscriptionRecord,
} from '../../services/adminTeamWorkspaceService';
import { currencyService } from '../../services/currencyService';

const dateKey = (value?: string) => {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
};

const daysUntil = (value?: string) => {
  const key = dateKey(value);
  if (!key) return null;
  const target = new Date(`${key}T00:00:00.000Z`).getTime();
  const todayKey = new Date().toISOString().slice(0, 10);
  const base = new Date(`${todayKey}T00:00:00.000Z`).getTime();
  return Math.round((target - base) / 86400000);
};
const LOAD_TIMEOUT_MS = 12000;
const withTimeout = <T,>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error(`Timeout de chargement (${label})`)), timeoutMs);
    }),
  ]);

const TodayManager = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [teamRecords, setTeamRecords] = useState<Record<string, TeamSubscriptionRecord>>({});
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      try {
        const [ordersResult, subscriptionsResult, teamRowsResult] = await Promise.allSettled([
          withTimeout(adminOrderService.getOrders(true), LOAD_TIMEOUT_MS, 'commandes'),
          withTimeout(
            adminSubscriptionService.getSubscriptions(true),
            LOAD_TIMEOUT_MS,
            'abonnements'
          ),
          withTimeout(adminTeamWorkspaceService.getRecords(), LOAD_TIMEOUT_MS, 'equipe'),
        ]);

        const loadErrors: string[] = [];

        if (ordersResult.status === 'fulfilled') {
          setOrders(ordersResult.value);
        } else {
          console.error(ordersResult.reason);
          setOrders([]);
          loadErrors.push('commandes');
        }

        if (subscriptionsResult.status === 'fulfilled') {
          setSubscriptions(subscriptionsResult.value);
        } else {
          console.error(subscriptionsResult.reason);
          setSubscriptions([]);
          loadErrors.push('abonnements');
        }

        if (teamRowsResult.status === 'fulfilled') {
          setTeamRecords(
            teamRowsResult.value.reduce<Record<string, TeamSubscriptionRecord>>((acc, row) => {
              acc[row.subscriptionId] = row;
              return acc;
            }, {})
          );
        } else {
          console.error(teamRowsResult.reason);
          setTeamRecords({});
          loadErrors.push('equipe');
        }

        if (loadErrors.length > 0) {
          toast.error(`Chargement partiel: ${loadErrors.join(', ')}`);
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : 'Erreur chargement dashboard aujourd hui');
      } finally {
        setIsLoading(false);
      }
    };
    load().catch(() => undefined);
  }, []);

  useEffect(() => currencyService.subscribe(setDisplayCountry), []);

  const todaysTasks = useMemo(() => {
    const today = dateKey(new Date().toISOString());
    return Object.entries(teamRecords).flatMap(([subscriptionId, record]) =>
      (record.tasks || [])
        .filter((task) => task.status !== 'done' && dateKey(task.dueDate) === today)
        .map((task) => ({ ...task, subscriptionId }))
    );
  }, [teamRecords]);

  const overdueTasks = useMemo(
    () =>
      Object.entries(teamRecords).flatMap(([subscriptionId, record]) =>
        (record.tasks || [])
          .filter((task) => task.status !== 'done')
          .filter((task) => {
            const diff = daysUntil(task.dueDate);
            return diff !== null && diff < 0;
          })
          .map((task) => ({ ...task, subscriptionId }))
      ),
    [teamRecords]
  );

  const paymentsAwaiting = orders.filter((order) => order.paymentStatus === 'awaiting_payment');
  const renewalsSoon = subscriptions.filter((subscription) => {
    const d = daysUntil(subscription.nextBillingDate);
    return d !== null && d >= 0 && d <= 10;
  });

  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);
  const awaitingAmount = paymentsAwaiting.reduce(
    (sum, order) =>
      sum +
      currencyService.convertToDisplayAmount(order.amount, order.currencyCode, displayCountry),
    0
  );

  if (isLoading) {
    return <div className="py-16 text-center text-gray-600">Chargement de la vue Aujourd hui...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-2xl border border-gray-200 bg-white p-5">
        <h2 className="text-2xl font-bold text-gray-900">Vue Aujourd hui agence</h2>
        <p className="mt-2 text-sm text-gray-600">
          Priorites operationnelles: paiements a suivre, abonnements qui renouvellent, taches equipe dues.
        </p>
        <div className="mt-5 grid gap-4 md:grid-cols-4">
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Paiements en attente</div>
            <div className="mt-1 text-3xl font-bold text-amber-600">{paymentsAwaiting.length}</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Montant a encaisser</div>
            <div className="mt-1 text-3xl font-bold text-gray-900">
              {currencyService.formatCurrencyAmount(awaitingAmount, displayCurrencyCode, {
                country: displayCountry,
                display: 'code',
              })}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Renouvellements (10j)</div>
            <div className="mt-1 text-3xl font-bold text-indigo-700">{renewalsSoon.length}</div>
          </div>
          <div className="rounded-xl border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Taches due + retard</div>
            <div className="mt-1 text-3xl font-bold text-red-600">{todaysTasks.length + overdueTasks.length}</div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <h3 className="text-lg font-semibold text-gray-900">Taches dues aujourd hui</h3>
          <div className="mt-3 space-y-2">
            {todaysTasks.length === 0 ? (
              <p className="text-sm text-gray-500">Aucune tache due aujourd hui.</p>
            ) : (
              todaysTasks.slice(0, 12).map((task) => {
                const sub = subscriptions.find((entry) => entry.id === task.subscriptionId);
                return (
                  <div key={task.id} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                    <div className="font-medium text-gray-900">{task.title}</div>
                    <div className="text-gray-600">{sub?.subscriptionNumber} · {sub?.clientName}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <h3 className="text-lg font-semibold text-gray-900">Paiements en attente</h3>
          <div className="mt-3 space-y-2">
            {paymentsAwaiting.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun paiement en attente.</p>
            ) : (
              paymentsAwaiting.slice(0, 12).map((order) => (
                <div key={order.id} className="rounded-lg border border-gray-200 px-3 py-2 text-sm">
                  <div className="font-medium text-gray-900">{order.orderNumber} · {order.clientName}</div>
                  <div className="text-gray-600">
                    {currencyService.formatCurrencyAmount(order.amount, order.currencyCode, { display: 'code' })}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TodayManager;
